// Files and clipboard contents from this computer, for programs on the machine.
//
// A file dropped on a terminal pane, or pasted when the clipboard holds files and no text, is
// uploaded to the machine and its path there is pasted into the session, the way a terminal
// types a dropped file's path locally. Pasting a single image also puts it on the machine's
// clipboard, and Ctrl+V (the key Linux programs use to read the clipboard) first copies this
// computer's clipboard there. Nothing here depends on which program is running.

import { machineName, store, toast } from '../store';
import type { SessionTerm } from './SessionTerm';
import { postFile, uploadRunning } from './upload';

export const MAX_UPLOAD = 100 * 1024 * 1024;
export const MAX_CLIPBOARD = 20 * 1024 * 1024;
const CLIPBOARD_WAIT_MS = 3000;
const NOTE_MS = 6000;
/** A failed upload's banner goes by itself after this long (it can be dismissed sooner). */
const FAILED_MS = 6000;
/** An upload shows progress only if it is still running after this long (a fast one needs none: the pasted path is the feedback). */
const PROGRESS_AFTER_MS = 1000;
/** The banner's bar and bytes follow the upload at most this often. */
const PROGRESS_EVERY_MS = 100;

/** Backslash-escape a path the way terminals type a dropped file. */
export function shellEscape(p: string): string {
  return p.replace(/[ \t'"\\$`!&*()[\]{};<>?|#~]/g, (c) => `\\${c}`);
}

export function formatSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

let transferId = 1;

function addTransfer(session: number, text: string) {
  const id = transferId++;
  store.set((s) => ({
    transfers: [...s.transfers.filter((t) => t.session !== session), { id, session, kind: 'notice', text, at: Date.now() }],
  }));
  setTimeout(() => dismissTransfer(id), NOTE_MS);
}

export function dismissTransfer(id: number) {
  store.set((s) => (s.transfers.some((t) => t.id === id) ? { transfers: s.transfers.filter((t) => t.id !== id) } : {}));
}

let uploadId = 1;

/** Show an upload in its session's pane (one at a time per session). */
function addUpload(session: number, name: string, total: number, sent: number, error?: string): number {
  const id = uploadId++;
  store.set((s) => ({ uploads: [...s.uploads.filter((u) => u.session !== session), { id, session, name, sent, total, error }] }));
  if (error) setTimeout(() => dismissUpload(id), FAILED_MS);
  return id;
}

function patchUpload(id: number, patch: { sent?: number; error?: string }) {
  store.set((s) => (s.uploads.some((u) => u.id === id) ? { uploads: s.uploads.map((u) => (u.id === id ? { ...u, ...patch } : u)) } : {}));
  if (patch.error) setTimeout(() => dismissUpload(id), FAILED_MS);
}

export function dismissUpload(id: number) {
  store.set((s) => (s.uploads.some((u) => u.id === id) ? { uploads: s.uploads.filter((u) => u.id !== id) } : {}));
}

async function post(path: string, body: Blob | string, type: string): Promise<Record<string, unknown>> {
  const r = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': type, 'X-Cmx-Upload': '1' },
    body,
  });
  let j: Record<string, unknown> = {};
  try {
    j = await r.json();
  } catch {
    /* not JSON */
  }
  if (!r.ok) throw new Error(typeof j.error === 'string' ? j.error : `HTTP ${r.status}`);
  return j;
}

/** Upload one file; resolves to its path on the machine. */
export async function uploadFile(f: File, opts: { signal?: AbortSignal; onProgress?: (sent: number) => void } = {}): Promise<string> {
  const j = await postFile(`/api/upload?name=${encodeURIComponent(f.name || 'file')}`, f, f.type || 'application/octet-stream', opts.signal ?? new AbortController().signal, opts.onProgress ?? (() => {}));
  if (typeof j.path !== 'string') throw new Error('the cloud machine sent no path');
  return j.path;
}

/** Put an image or text on the machine's clipboard. */
export async function setMachineClipboard(body: Blob | string, type: string): Promise<void> {
  await post('/api/clipboard', body, type);
}

// Uploads into one session run one after another, so paths arrive in drop order.
const chains = new Map<number, Promise<void>>();

/**
 * Upload files into a session and paste their machine paths there. Input typed meanwhile is
 * held and sent after the paths; Esc typed alone stops the upload instead (upload.ts), as does
 * the banner's Cancel, and a stopped or failed one pastes nothing and lets the held input go.
 * An upload that stops moving fails by itself (UPLOAD_STALL_MS). `folders` counts dropped
 * folders (not uploaded).
 */
export function sendFiles(t: SessionTerm, files: File[], opts: { folders?: number; alsoClipboard?: boolean } = {}) {
  const sid = t.sid;
  if (opts.folders) addTransfer(sid, 'Folders aren’t uploaded · use scp for folders (cmx ssh-config sets it up)');
  const big = files.filter((f) => f.size > MAX_UPLOAD);
  if (big.length) addUpload(sid, big[0].name, big[0].size, 0, `${big[0].name} is over ${MAX_UPLOAD / 1024 / 1024} MB · use scp for big files`);
  const ok = files.filter((f) => f.size <= MAX_UPLOAD);
  if (!ok.length) return;
  t.hold();
  const run = async () => {
    const total = ok.reduce((n, f) => n + f.size, 0);
    const name = ok.length === 1 ? ok[0].name : `${ok.length} files`;
    const stop = new AbortController();
    const over = uploadRunning(sid, () => stop.abort());
    let done = 0; // bytes of the files already uploaded
    let current = 0; // bytes of the one going now
    let tid = 0;
    let shownAt = 0;
    const sent = () => Math.min(total, done + current);
    const slow = setTimeout(() => {
      tid = addUpload(sid, name, total, sent());
    }, PROGRESS_AFTER_MS);
    const paths: string[] = [];
    let failed = '';
    try {
      for (const f of ok) {
        current = 0;
        const onProgress = (n: number) => {
          current = n;
          if (!tid || Date.now() - shownAt < PROGRESS_EVERY_MS) return;
          shownAt = Date.now();
          patchUpload(tid, { sent: sent() });
        };
        paths.push(await uploadFile(f, { signal: stop.signal, onProgress }));
        done += f.size;
        current = 0;
      }
      over(); // the files went: Esc is a key again
      const img = ok.length === 1 && ok[0].type.startsWith('image/') ? ok[0] : null;
      if (opts.alsoClipboard && img && img.size <= MAX_CLIPBOARD) {
        await setMachineClipboard(img, img.type).catch(() => {});
      }
    } catch (e) {
      paths.length = 0; // stopped or failed: no path is pasted
      if (!stop.signal.aborted) failed = e instanceof Error ? e.message : String(e);
    } finally {
      over();
      clearTimeout(slow);
      if (failed) {
        if (tid) patchUpload(tid, { error: failed });
        else addUpload(sid, name, total, sent(), failed);
      } else if (tid) dismissUpload(tid);
      t.release(paths.length ? { paste: paths.map(shellEscape).join(' ') } : {});
    }
  };
  const next = (chains.get(sid) ?? Promise.resolve()).then(run, run);
  chains.set(sid, next);
}

/** The files of a paste, when it has files and no text (otherwise the normal text paste runs). */
export function filesFromPaste(e: ClipboardEvent): File[] | null {
  const dt = e.clipboardData;
  if (!dt || !dt.files || dt.files.length === 0) return null;
  if (dt.getData('text/plain')) return null;
  return Array.from(dt.files);
}

/** Split a drop into files and folders. */
export function filesFromDrop(dt: DataTransfer): { files: File[]; folders: number } {
  const files: File[] = [];
  let folders = 0;
  const items = dt.items ? Array.from(dt.items) : [];
  if (items.length) {
    for (const it of items) {
      if (it.kind !== 'file') continue;
      const entry = (it as DataTransferItem & { webkitGetAsEntry?: () => { isDirectory: boolean } | null }).webkitGetAsEntry?.();
      if (entry?.isDirectory) {
        folders++;
        continue;
      }
      const f = it.getAsFile();
      if (f) files.push(f);
    }
  } else {
    files.push(...Array.from(dt.files));
  }
  return { files, folders };
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | undefined> {
  return Promise.race([p, new Promise<undefined>((r) => setTimeout(() => r(undefined), ms))]);
}

/**
 * Ctrl+V: copy this computer's clipboard (an image, else text) to the machine's clipboard,
 * then send Ctrl+V. Never swallows the key: without clipboard access, or after a few
 * seconds, Ctrl+V goes through anyway. Input typed meanwhile follows it.
 */
export function ctrlV(t: SessionTerm) {
  t.hold();
  const sync = async () => {
    if (!navigator.clipboard?.read) return;
    const items = await navigator.clipboard.read();
    for (const item of items) {
      const img = item.types.find((ty) => ty.startsWith('image/'));
      if (img) {
        const blob = await item.getType(img);
        if (blob.size <= MAX_CLIPBOARD) await setMachineClipboard(blob, img);
        return;
      }
    }
    for (const item of items) {
      if (item.types.includes('text/plain')) {
        const text = await (await item.getType('text/plain')).text();
        if (text && text.length <= MAX_CLIPBOARD) await setMachineClipboard(text, 'text/plain');
        return;
      }
    }
  };
  void withTimeout(
    sync().catch(() => {}),
    CLIPBOARD_WAIT_MS,
  ).finally(() => t.release({ input: '\x16' }));
}

/**
 * Something was copied on the machine and this tab is where the person typed last: put it on
 * this computer's clipboard (agent clipshare.go). Browsers only let a page write the clipboard
 * while it has focus (Safari: only from a click), so when the write is refused the notice
 * offers Copy instead.
 */
export function machineCopied(e: { text?: string; image?: string }) {
  const what = e.image ? 'An image' : 'Text';
  const write = async () => {
    if (e.image) {
      const png = await (await fetch(`data:image/png;base64,${e.image}`)).blob();
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
    } else await navigator.clipboard.writeText(e.text ?? '');
  };
  write().then(
    () => toast({ key: 'clip', title: `⧉ ${what} copied on ${machineName()}`, body: 'Now on this computer’s clipboard.' }, 3000),
    () =>
      toast(
        {
          key: 'clip',
          title: `⧉ ${what} copied on ${machineName()}`,
          body: 'Copy it to this computer’s clipboard?',
          action: { label: 'Copy', run: () => void write().catch(() => undefined) },
        },
        10000,
      ),
  );
}
